<template>
    <SidebarPanel
        :is-open="isOpen"
        :title="plugin.title"
        :return-focus-selector="`[data-reader-plugin-button='${plugin.key}']`"
        side="left"
        persistent
        resizable
        mobile-fullscreen
        flush
        :dusk="`ReaderSidebarPlugin-${plugin.key}`"
        @close="trackTogglePlugin"
    >
        <div class="flex min-h-0 flex-1 flex-col" :dusk="`ReaderSidebarPluginContent-${plugin.key}`">
            <div v-if="isDebugMode" class="debugMode">
                <div class="debugger-controls">
                    <input
                        v-model="testEventMessage"
                        type="text"
                        placeholder="Enter your message"
                        aria-label="Debug test message"
                    />
                    <button
                        class="focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                        @click="pushTestEvent"
                    >
                        send
                    </button>
                    <button
                        class="reload-button focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                        @click="reloadIframe"
                    >
                        reload
                    </button>
                </div>
            </div>
            <div class="relative flex min-h-0 flex-1 flex-col">
                <iframe
                    ref="pluginIframe"
                    :title="plugin.title"
                    :class="['min-h-0 w-full flex-1 border-0', { invisible: loadState !== 'ready' }]"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                ></iframe>
                <div v-if="loadState === 'loading'" class="absolute inset-0 flex items-center justify-center">
                    <SimpleSpinner />
                </div>
                <div
                    v-else-if="loadState === 'failed'"
                    role="alert"
                    class="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-theme-text"
                    :dusk="`ReaderSidebarPluginLoadFailed-${plugin.key}`"
                >
                    <p :id="`reader-plugin-load-failed-${plugin.key}`">{{ strings.loadFailed }}</p>
                    <BaseButton
                        variant="primary"
                        type="button"
                        :aria-describedby="`reader-plugin-load-failed-${plugin.key}`"
                        @click="reloadIframe"
                    >
                        {{ strings.retry }}
                    </BaseButton>
                </div>
            </div>
        </div>
    </SidebarPanel>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { v7 as uuidv7 } from 'uuid';
import SidebarPanel from '../Components/SidebarPanel.vue';
import BaseButton from '../Components/buttons/BaseButton.vue';
import SimpleSpinner from '../Utilities/SimpleSpinner.vue';
import isDarkTheme from '../../js/utils/isDarkTheme';
import { interactionEventsTracker } from '../../../src/js/communication/analytics';

/** How long a plugin may take to load before its panel offers a retry. */
const PLUGIN_LOAD_TIMEOUT_MS = 10000;

export default {
    components: {
        SidebarPanel,
        BaseButton,
        SimpleSpinner,
    },
    props: {
        /** One of `shownReaderPlugins`: its key, src and title. */
        plugin: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            nonce: null,
            // Here for debugging purposes
            // iframeSrc: 'https://volpe.localhost/third-party/third-party-plugin-debugger.html',
            iframeSrc: null,
            iframeSrcWithNonce: '',
            // idle until the first open, then loading, ready or failed.
            loadState: 'idle',
            loadTimeout: null,
            testEventMessage: '',
        };
    },
    computed: {
        ...mapState(['ui', 'fileType', 'localizations', 'lang']),
        ...mapGetters(['isEpub', 'pluginCurrentPages', 'pluginTotalPages', 'isReaderPluginOpen']),
        isOpen() {
            return this.isReaderPluginOpen(this.plugin.key);
        },
        iframeLoaded() {
            return this.loadState === 'ready';
        },
        strings() {
            return this.localizations[this.lang]?.readerPlugin ?? this.localizations.en.readerPlugin;
        },
        iframeSrcSeparator() {
            // Determine the separator based on whether '?' is found in the src
            return this.iframeSrc?.includes('?') ? '&' : '?';
        },
        isDebugMode() {
            /**
             * Enable debug mode and it's UI when the iframe's SRC is from our debugger.
             * This is not a precise check, it could be triggered by other URLs and
             * that's fine.
             */
            return this.iframeSrc?.includes('third-party-plugin-debugger');
        },
        shouldInitialize() {
            // The parent only mounts this component when the plugin is enabled and has a src.
            // EPUBs also wait for the current pages to resolve: the init
            // handshake carries them, and handing the plugin an unknown
            // reading point would misaddress its first requests. Only
            // fixed-layout can actually be pending here (its spread feed
            // starts empty); the reflowable readout always holds a page.
            return this.isOpen && this.loadState === 'idle' && this.currentPagesResolved;
        },
        currentPagesResolved() {
            return !this.isEpub || this.pluginCurrentPages.length > 0;
        },
        currentTheme() {
            return this.ui.themeSelected || 'light';
        },
        /**
         * Origin the plugin iframe is served from, used to authenticate inbound messages.
         * Returns null when there is no src yet or it cannot be parsed: onPluginMessage
         * then rejects every message, which is the intended fail-closed behavior.
         * @returns {string|null}
         */
        pluginOrigin() {
            if (!this.iframeSrc) {
                return null;
            }
            try {
                return new URL(this.iframeSrc, window.location.href).origin;
            } catch {
                return null;
            }
        },
    },
    watch: {
        pluginCurrentPages(currentPages, lastCurrentPages) {
            console.debug('[ReaderSidebarPlugin] Page changed', { currentPages, lastCurrentPages });
            if (!this.iframeLoaded) {
                console.debug('[ReaderSidebarPlugin] Plugin iframe not loaded, skipping page change event');
                return;
            }
            // Getter recomputes can yield a new array with the same pages;
            // only a real page change is worth an event.
            const samePages =
                currentPages.length === lastCurrentPages.length &&
                currentPages.every((page, index) => page === lastCurrentPages[index]);
            if (samePages) {
                return;
            }
            this.pushPageChange(currentPages);
        },
        shouldInitialize(shouldInit) {
            console.debug('[ReaderSidebarPlugin] Should initialize changed:', shouldInit);
            if (shouldInit) {
                console.debug('[ReaderSidebarPlugin] Initializing iframe for the first time');
                this.initializeIframe();
            }
        },
        isOpen(open) {
            // Every way a panel opens or closes ends here: its button, another
            // panel taking its place, or the plugin asking to close.
            interactionEventsTracker.trackPanel('plugin', open ? 'open' : 'close', this.plugin.key);
        },
        currentTheme(newTheme, oldTheme) {
            console.debug('[ReaderSidebarPlugin] Theme changed', { newTheme, oldTheme });
            if (!this.iframeLoaded) {
                console.debug('[ReaderSidebarPlugin] Plugin iframe not loaded, skipping theme change event');
                return;
            }
            // Read the palette after the DOM applied the new data-theme, or the
            // resolved tokens would still belong to the previous theme.
            this.$nextTick(() => this.pushThemeChange(newTheme));
        },
    },
    mounted() {
        window.addEventListener('message', this.onPluginMessage);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.onPluginMessage);
        clearTimeout(this.loadTimeout);
    },
    methods: {
        ...mapActions(['trackEvent', 'togglePlugin']),
        /**
         * Handles upward postMessage events emitted by the plugin iframe.
         * Only messages sent by the plugin iframe's own window, from the plugin
         * origin and carrying the current nonce, are accepted.
         * @param {MessageEvent} event
         */
        onPluginMessage(event) {
            if (!this.pluginOrigin || event.origin !== this.pluginOrigin) {
                return;
            }
            if (event.source !== this.$refs.pluginIframe?.contentWindow) {
                return;
            }

            const message = event.data;
            if (!message || message.type !== 'plugin-event' || message.nonce !== this.nonce) {
                return;
            }

            if (message.name === 'close-request' && this.isOpen) {
                this.trackTogglePlugin();
            }
            if (message.name === 'request-theme') {
                this.pushThemeChange(this.currentTheme);
            }
        },
        /**
         * Reads the resolved CSS values of the active reader palette.
         * @returns {Object} Token map consumed by the plugin iframe
         */
        resolveThemeTokens() {
            const styles = getComputedStyle(this.$el);
            const readToken = name => styles.getPropertyValue(name).trim();

            return {
                background: readToken('--theme-background-color'),
                text: readToken('--theme-text-color'),
                accent: readToken('--theme-accent-color'),
                textOnAccent: readToken('--theme-accent-on-color'),
                accentBg: readToken('--theme-accent-bg'),
                border: readToken('--theme-border-color'),
                toolbarIcon: readToken('--theme-toolbar-icons-color'),
            };
        },
        /**
         * Builds the theme portion shared by the init and theme-change payloads.
         * @param {string} theme
         * @returns {Object} darkMode flag, theme name and resolved palette tokens
         */
        buildThemePayload(theme) {
            return {
                darkMode: isDarkTheme(theme),
                theme,
                tokens: this.resolveThemeTokens(),
            };
        },
        initIframeConnection() {
            const payload = {
                currentPages: this.pluginCurrentPages,
                totalPages: this.pluginTotalPages,
                contentType: this.fileType,
                ...this.buildThemePayload(this.currentTheme),
            };
            console.debug('[ReaderSidebarPlugin] Iframe loaded, establishing connection', { payload });
            clearTimeout(this.loadTimeout);
            this.loadState = 'ready';
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'init',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload,
                },
                this.iframeSrcWithNonce
            );
        },
        pushPageChange(currentPages) {
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'page-change',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload: {
                        currentPages,
                    },
                },
                this.iframeSrcWithNonce
            );
        },
        pushThemeChange(theme) {
            this.$refs.pluginIframe.contentWindow.postMessage(
                {
                    type: 'event',
                    name: 'theme-change',
                    id: uuidv7(),
                    timestamp: Date.now(),
                    nonce: this.nonce,
                    payload: this.buildThemePayload(theme),
                },
                this.iframeSrcWithNonce
            );
        },
        pushTestEvent() {
            const message = {
                type: 'event',
                name: 'test',
                id: uuidv7(),
                timestamp: Date.now(),
                nonce: this.nonce,
                payload: {
                    currentPages: this.pluginCurrentPages,
                },
            };

            if (this.testEventMessage.trim() !== '') {
                message.payload.testEventMessage = this.testEventMessage;
                this.testEventMessage = '';
            }

            this.$refs.pluginIframe.contentWindow.postMessage(message, this.iframeSrcWithNonce);
        },
        trackTogglePlugin() {
            this.trackEvent({
                action: 'Plugin - toggle',
                trigger: 'click',
                page: '',
                source: 'close corner',
            });
            this.togglePlugin(this.plugin.key);
        },
        resetIframeSrc() {
            this.nonce = uuidv7();
            this.iframeSrcWithNonce = `${this.iframeSrc}${this.iframeSrcSeparator}nonce=${this.nonce}`;
            this.loadState = 'loading';
            clearTimeout(this.loadTimeout);
            this.loadTimeout = setTimeout(() => {
                if (this.loadState === 'loading') this.loadState = 'failed';
            }, PLUGIN_LOAD_TIMEOUT_MS);
            this.$refs.pluginIframe.src = this.iframeSrcWithNonce;
        },
        reloadIframe() {
            this.resetIframeSrc();
        },
        initializeIframe() {
            // Initialize the iframe source only when needed
            this.iframeSrc = this.plugin.src;
            this.resetIframeSrc();
            this.$refs.pluginIframe.onload = this.initIframeConnection;
        },
    },
};
</script>

<style lang="scss" scoped>
.debugMode {
    background-color: #f9f9f9;
    border-top: solid 5px;
    border-image: repeating-linear-gradient(-75deg, yellow, yellow 10px, black 10px, black 20px) 20;

    .debugger-controls {
        display: flex;
        gap: 10px;
        margin: 10px 0;
        padding: 10px;
    }

    input {
        padding: 10px;
        border: 1px solid #dddddd;
        border-radius: 5px;
        font-size: 14px;
        flex: 1;

        &:focus-visible {
            outline: none;
            box-shadow: 0 0 0 2px #3b82f6;
        }
    }

    button {
        background-color: #007bff;
        color: #ffffff;
        border: none;
        padding: 10px 15px;
        font-size: 14px;
        cursor: pointer;
        border-radius: 5px;
        transition: background-color 0.3s;

        &:hover {
            background-color: #0056b3;
        }
    }

    .reload-button {
        background-color: #28a745;

        &:hover {
            background-color: #218838;
        }
    }
}
</style>
